import * as R from 'ramda'
import {
  all,
  call,
  delay,
  put,
  race,
  select,
  take,
  takeLatest,
  takeLeading,
} from 'redux-saga/effects'
import { v4 as uuid } from 'uuid'
import type { ApiError } from '@pbt/pbt-ui-components'

import * as API from '~/api'
import { globalNavigate } from '~/components/GlobalHistory'
import DialogNames from '~/constants/DialogNames'
import DocumentDialogStates from '~/constants/DocumentDialogStates'
import FeatureToggle from '~/constants/featureToggle'
import type { Document, DocumentToSend } from '~/types'
import { downloadFile } from '~/utils/file'

import {
  type createDocument,
  createDocumentFailure,
  createDocumentSuccess,
  type deleteDocument,
  deleteDocumentFailure,
  deleteDocumentSuccess,
  type editDocument,
  editDocumentFailure,
  editDocumentSuccess,
  type fetchDocument,
  fetchDocumentFailure,
  type fetchDocuments,
  type fetchDocumentsByType,
  fetchDocumentsByTypeFailure,
  fetchDocumentsByTypeSuccess,
  fetchDocumentsFailure,
  fetchDocumentsList,
  fetchDocumentsListFailure,
  fetchDocumentsListSuccess,
  fetchDocumentsSuccess,
  fetchDocumentSuccess,
  type fetchMoreItemsForDocumentsList,
  fetchMoreItemsForDocumentsListFailure,
  fetchMoreItemsForDocumentsListSuccess,
  type fetchResolvedDocument,
  type fetchResolvedDocumentBody,
  fetchResolvedDocumentBodyFailure,
  fetchResolvedDocumentBodySuccess,
  fetchResolvedDocumentFailure,
  type fetchResolvedDocuments,
  fetchResolvedDocumentsFailure,
  fetchResolvedDocumentsSuccess,
  fetchResolvedDocumentSuccess,
  type generateDocumentInstances,
  generateDocumentInstancesFailure,
  generateDocumentInstancesSuccess,
  type generateFileForDocumentTemplate,
  generateFileForDocumentTemplateFailure,
  generateFileForDocumentTemplateSuccess,
  type generatePdfDownload,
  generatePdfDownloadFailure,
  generatePdfDownloadSuccess,
  type generatePdfPreview,
  generatePdfPreviewFailure,
  generatePdfPreviewSuccess,
} from '../actions/documents'
import {
  CREATE_DOCUMENT,
  CREATE_DOCUMENT_WITH_TEMPLATE,
  DELETE_DOCUMENT,
  EDIT_DOCUMENT,
  EDIT_DOCUMENT_WITH_TEMPLATE,
  FETCH_DOCUMENT,
  FETCH_DOCUMENTS,
  FETCH_DOCUMENTS_BY_TYPE,
  FETCH_DOCUMENTS_LIST,
  FETCH_MORE_ITEMS_FOR_DOCUMENTS_LIST,
  FETCH_RESOLVED_DOCUMENT,
  FETCH_RESOLVED_DOCUMENT_BODY,
  FETCH_RESOLVED_DOCUMENTS,
  GENERATE_DOCUMENT_INSTANCES,
  GENERATE_FILE_FOR_DOCUMENT_TEMPLATE,
  GENERATE_PDF_DOWNLOAD,
  GENERATE_PDF_PREVIEW,
  WS_DOCUMENT_INSTANCE_CREATE,
} from '../actions/types/documents'
import { openDialog } from '../duck/dialogs'
import { finishLoading, startLoading } from '../duck/progress'
import { registerWarnAlert } from '../duck/uiAlerts'
import { getFeatureToggle } from '../reducers/constants'
import requestAPI from './utils/requestAPI'
import updateEntities from './utils/updateEntities'

const TWO_MINUTES_MS = 2 * 60 * 1000

const createDocumentToSend = (document: Document): DocumentToSend => ({
  id: document.id,
  file: document.template?.file,
  document: new Blob(
    [
      JSON.stringify({
        ...document,
        template: R.omit(['file'], document.template),
      }),
    ],
    {
      type: 'application/json',
    },
  ),
})

export function* fetchDocumentsListSaga({
  from,
  to,
  query,
}: ReturnType<typeof fetchDocumentsList>) {
  try {
    yield put(startLoading('documents'))
    const {
      result: { data: list, totalCount },
      entities,
    } = yield* requestAPI(API.fetchDocumentsList, from, to, query)
    yield call(updateEntities, entities)
    yield put(fetchDocumentsListSuccess(list, totalCount))
    yield put(finishLoading('documents'))
  } catch (error) {
    yield put(fetchDocumentsListFailure(error as ApiError))
    yield put(finishLoading('documents'))
  }
}

export function* fetchMoreItemsForDocumentsListSaga({
  from,
  to,
  query,
}: ReturnType<typeof fetchMoreItemsForDocumentsList>) {
  try {
    const {
      result: { data: list, totalCount },
      entities,
    } = yield* requestAPI(API.fetchDocumentsList, from, to, query)
    yield call(updateEntities, entities)
    yield put(fetchMoreItemsForDocumentsListSuccess(list, totalCount, from))
  } catch (error) {
    yield put(fetchMoreItemsForDocumentsListFailure(error as ApiError))
  }
}

export function* fetchDocumentSaga({ id }: ReturnType<typeof fetchDocument>) {
  try {
    const { entities } = yield* requestAPI(API.fetchDocument, id)
    yield call(updateEntities, entities)
    yield put(fetchDocumentSuccess())
  } catch (error) {
    yield put(fetchDocumentFailure(error as ApiError))
  }
}

export function* fetchDocumentsSaga({
  ids,
}: ReturnType<typeof fetchDocuments>) {
  try {
    const { entities } = yield* requestAPI(API.fetchDocuments, ids)
    yield call(updateEntities, entities)
    yield put(fetchDocumentsSuccess())
  } catch (error) {
    yield put(fetchDocumentsFailure(error as ApiError))
  }
}

export function* createDocumentSaga({
  document,
}: ReturnType<typeof createDocument>) {
  try {
    const isInputFormsEnabled: boolean = yield select(
      getFeatureToggle(FeatureToggle.INPUT_FORMS_DOCUMENT),
    )
    const { result, entities } = yield* requestAPI(API.createDocument, document)
    yield call(updateEntities, entities)
    yield put(fetchDocumentsList())
    yield put(createDocumentSuccess(result))
    if (isInputFormsEnabled) {
      globalNavigate(`/admin/catalog/documents/${result}`)
    }
  } catch (error) {
    yield put(createDocumentFailure(error as ApiError))
  }
}

export function* createDocumentWithTemplateSaga({
  document,
}: ReturnType<typeof createDocument>) {
  try {
    const documentToSend = createDocumentToSend(document)
    const { result, entities } = yield* requestAPI(
      API.createDocumentWithTemplate,
      documentToSend,
    )
    yield call(updateEntities, entities)
    yield put(fetchDocumentsList())
    yield put(createDocumentSuccess(result))
  } catch (error) {
    yield put(createDocumentFailure(error as ApiError))
  }
}

export function* editDocumentSaga({
  document,
}: ReturnType<typeof editDocument>) {
  try {
    const { entities } = yield* requestAPI(API.editDocument, document)
    yield call(updateEntities, entities)
    yield put(editDocumentSuccess())
  } catch (error) {
    yield put(editDocumentFailure(error as ApiError))
  }
}

export function* editDocumentWithTemplateSaga({
  document,
}: ReturnType<typeof editDocument>) {
  try {
    const documentToSend = createDocumentToSend(document)
    const { entities } = yield* requestAPI(
      API.editDocumentWithTemplate,
      documentToSend,
    )
    yield call(updateEntities, entities)
    yield put(editDocumentSuccess())
  } catch (error) {
    yield put(editDocumentFailure(error as ApiError))
  }
}

export function* deleteDocumentSaga({
  documentId,
}: ReturnType<typeof deleteDocument>) {
  try {
    const isInputFormsEnabled: boolean = yield select(
      getFeatureToggle(FeatureToggle.INPUT_FORMS_DOCUMENT),
    )
    yield* requestAPI(API.deleteDocument, documentId)
    yield put(deleteDocumentSuccess(documentId))
    if (isInputFormsEnabled) {
      globalNavigate(`/admin/catalog/documents`)
    }
  } catch (error) {
    yield put(deleteDocumentFailure(error as ApiError))
  }
}

export function* fetchResolvedDocumentBodySaga({
  body,
}: ReturnType<typeof fetchResolvedDocumentBody>) {
  try {
    const result = yield* requestAPI(API.fetchResolvedDocumentBody, body)
    yield put(fetchResolvedDocumentBodySuccess(result.body))
  } catch (error) {
    yield put(fetchResolvedDocumentBodyFailure(error as ApiError))
  }
}

export function* fetchResolvedDocumentSaga({
  document,
}: ReturnType<typeof fetchResolvedDocument>) {
  try {
    const result = yield* requestAPI(API.fetchResolvedDocument, document)
    yield put(
      fetchResolvedDocumentSuccess({
        name: result.name,
        template: { documentElements: result.documentElements },
      } as Document),
    )
  } catch (error) {
    yield put(fetchResolvedDocumentFailure(error as ApiError))
  }
}

export function* fetchDocumentsByTypeSaga({
  documentType,
  patientId,
  eventId,
  skipFileTemplates,
}: ReturnType<typeof fetchDocumentsByType>) {
  try {
    const { result, entities } = yield* requestAPI(
      API.fetchDocumentsByType,
      documentType,
      patientId,
      eventId,
      skipFileTemplates,
    )
    yield call(updateEntities, entities)
    yield put(fetchDocumentsByTypeSuccess(result))
  } catch (error) {
    yield put(fetchDocumentsByTypeFailure(error as ApiError))
  }
}

export function* fetchResolvedDocumentsSaga({
  documentIds,
  context,
  convertToTextDocument,
}: ReturnType<typeof fetchResolvedDocuments>) {
  try {
    const { entities } = yield* requestAPI(
      API.fetchResolvedDocuments,
      documentIds,
      context,
      convertToTextDocument,
    )
    yield call(updateEntities, entities)
    yield put(fetchResolvedDocumentsSuccess())
  } catch (error) {
    yield put(fetchResolvedDocumentsFailure(error as ApiError))
  }
}

export function* generateFileForDocumentTemplateSaga({
  documentId,
}: ReturnType<typeof generateFileForDocumentTemplate>) {
  try {
    const generatedFile = yield* requestAPI(
      API.generateDocumentTemplateFile,
      documentId,
    )
    yield put(generateFileForDocumentTemplateSuccess(generatedFile))
  } catch (error) {
    yield put(generateFileForDocumentTemplateFailure(error as ApiError))
  }
}

export function* generatePdfPreviewSaga({
  document,
}: ReturnType<typeof generatePdfPreview>) {
  try {
    yield* requestAPI(API.generateDocumentPDF, document)

    // Wait for the WebSocket response
    const { socketResponse, timeout } = yield race({
      socketResponse: take(WS_DOCUMENT_INSTANCE_CREATE),
      timeout: delay(TWO_MINUTES_MS),
    })

    if (timeout) {
      throw new Error('WebSocket response timed out after 2 minutes.')
    }

    // Process the WebSocket response
    const { body } = socketResponse
    const { errorMessage } = body

    if (!errorMessage) {
      const { name, extension, fileUrl } = body

      yield put(
        openDialog({
          name: DialogNames.DOCUMENT,
          id: uuid(),
          props: {
            previewOnly: true,
            document: {
              extension,
              name,
              fileUrl,
            } as Document,
            step: DocumentDialogStates.PREVIEW,
            disableCloseAfterUpdate: true,
            PreviewProps: {
              hideTopButtons: true,
              view: true,
              showChangeFile: false,
              hideEmail: true,
              hidePrint: true,
            },
          },
        }),
      )
    } else {
      yield put(registerWarnAlert(errorMessage))
      yield put(generatePdfPreviewFailure(errorMessage))
    }

    yield put(generatePdfPreviewSuccess())
  } catch (error) {
    yield put(generatePdfPreviewFailure(error as ApiError))
  }
}

export function* generatePdfDownloadSaga({
  document,
}: ReturnType<typeof generatePdfDownload>) {
  try {
    yield* requestAPI(API.generateDocumentPDF, document)

    // Wait for the WebSocket response
    const { socketResponse, timeout } = yield race({
      socketResponse: take(WS_DOCUMENT_INSTANCE_CREATE),
      timeout: delay(TWO_MINUTES_MS),
    })

    if (timeout) {
      throw new Error('WebSocket response timed out after 2 minutes.')
    }

    // Process the WebSocket response
    const { body } = socketResponse
    const { errorMessage } = body

    if (!errorMessage) {
      const { name, extension, fileUrl } = body
      downloadFile({
        url: fileUrl,
        name,
        extension,
      })
      yield put(generatePdfDownloadSuccess())
    } else {
      yield put(registerWarnAlert(errorMessage))
      yield put(generatePdfDownloadFailure(errorMessage))
    }
  } catch (error) {
    yield put(generatePdfDownloadFailure(error as ApiError))
  }
}

export function* generateDocumentInstancesSaga({
  documentIds,
  context,
}: ReturnType<typeof generateDocumentInstances>) {
  try {
    const documentInstances = yield* requestAPI(
      API.generateDocumentInstances,
      documentIds,
      context,
    )
    yield put(generateDocumentInstancesSuccess(documentInstances))
  } catch (error) {
    yield put(generateDocumentInstancesFailure(error as ApiError))
  }
}

function* watchFetchDocumentsList() {
  yield takeLeading(FETCH_DOCUMENTS_LIST, fetchDocumentsListSaga)
}

function* watchFetchMoreItemsForDocumentsList() {
  yield takeLatest(
    FETCH_MORE_ITEMS_FOR_DOCUMENTS_LIST,
    fetchMoreItemsForDocumentsListSaga,
  )
}

function* watchFetchDocument() {
  yield takeLeading(FETCH_DOCUMENT, fetchDocumentSaga)
}

function* watchFetchDocuments() {
  yield takeLeading(FETCH_DOCUMENTS, fetchDocumentsSaga)
}

function* watchCreateDocument() {
  yield takeLeading(CREATE_DOCUMENT, createDocumentSaga)
}

function* watchCreateDocumentWithTemplate() {
  yield takeLeading(
    CREATE_DOCUMENT_WITH_TEMPLATE,
    createDocumentWithTemplateSaga,
  )
}

function* watchEditDocument() {
  yield takeLeading(EDIT_DOCUMENT, editDocumentSaga)
}

function* watchEditDocumentWithTemplate() {
  yield takeLeading(EDIT_DOCUMENT_WITH_TEMPLATE, editDocumentWithTemplateSaga)
}

function* watchDeleteDocument() {
  yield takeLeading(DELETE_DOCUMENT, deleteDocumentSaga)
}

function* watchFetchResolvedDocumentBody() {
  yield takeLeading(FETCH_RESOLVED_DOCUMENT_BODY, fetchResolvedDocumentBodySaga)
}

function* watchFetchResolvedDocument() {
  yield takeLeading(FETCH_RESOLVED_DOCUMENT, fetchResolvedDocumentSaga)
}

function* watchFetchDocumentsByType() {
  yield takeLeading(FETCH_DOCUMENTS_BY_TYPE, fetchDocumentsByTypeSaga)
}

function* watchFetchResolvedDocuments() {
  yield takeLeading(FETCH_RESOLVED_DOCUMENTS, fetchResolvedDocumentsSaga)
}

function* watchGenerateDocumentTemplateFile() {
  yield takeLeading(
    GENERATE_FILE_FOR_DOCUMENT_TEMPLATE,
    generateFileForDocumentTemplateSaga,
  )
}

function* watchGeneratePdfPreview() {
  yield takeLeading(GENERATE_PDF_PREVIEW, generatePdfPreviewSaga)
}

function* watchGeneratePdfDownload() {
  yield takeLeading(GENERATE_PDF_DOWNLOAD, generatePdfDownloadSaga)
}

function* watchGenerateDocumentInstances() {
  yield takeLeading(GENERATE_DOCUMENT_INSTANCES, generateDocumentInstancesSaga)
}

export default function* documentsSaga() {
  yield all([
    watchFetchDocumentsList(),
    watchFetchMoreItemsForDocumentsList(),
    watchFetchDocument(),
    watchFetchDocuments(),
    watchCreateDocument(),
    watchCreateDocumentWithTemplate(),
    watchEditDocument(),
    watchEditDocumentWithTemplate(),
    watchDeleteDocument(),
    watchFetchResolvedDocumentBody(),
    watchFetchResolvedDocument(),
    watchFetchDocumentsByType(),
    watchFetchResolvedDocuments(),
    watchGenerateDocumentTemplateFile(),
    watchGenerateDocumentInstances(),
    watchGeneratePdfPreview(),
    watchGeneratePdfDownload(),
  ])
}
